import { useEvent, useSyncRef } from '@react-aria/utils';
import * as React from 'react';
import {
    type AriaOverlayProps,
    FocusScope,
    type FocusScopeProps,
    useObjectRef,
    useOverlay,
} from 'react-aria';

import { assert } from '@gitbook/util-ts';

import { AnimatedStateContext } from '../animations';
import { checkIsElementContainsInOverlay } from '../util';
import { useContextMenuOutside } from '../util/useContextMenuOutside';
import { OverlayContext } from './OverlayContext';
import { getOverlayProps, OverlayTriggerContext } from './OverlayTrigger';

export const OverlayFocusContainedContext = React.createContext<boolean>(true);

export type OverlayProps = React.ComponentPropsWithoutRef<'div'> &
    Pick<FocusScopeProps, 'autoFocus' | 'contain' | 'restoreFocus'> &
    Pick<AriaOverlayProps, 'isDismissable'>;

/**
 * A container which renders an overlay such as a popover or modal in a portal,
 * and provides a focus scope for the child elements.
 */
export const Overlay = React.forwardRef(function Overlay(
    props: OverlayProps,
    forwardedRef: React.ForwardedRef<HTMLDivElement>
) {
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    const trigger = React.useContext(OverlayTriggerContext);
    assert(
        context !== null && animatedState !== null && trigger !== null,
        'Overlay must be used within a OverlayRoot'
    );

    const {
        autoFocus = true,
        contain = true,
        restoreFocus = true,
        isDismissable: isDismissableProp = true,
        ...domProps
    } = props;

    const isExiting = animatedState === 'exiting';
    const isExited = animatedState === 'exited';

    const ref = useObjectRef<HTMLDivElement>(forwardedRef);
    useSyncRef(context, ref);

    const hasInteractedOutside = useHasInteractedOutside(ref);

    const isFocusContained =
        React.useContext(OverlayFocusContainedContext) &&
        !isExiting &&
        !isExited &&
        !hasInteractedOutside &&
        contain;

    const isClosable = context.isClosable && !context.isPending;
    const isDismissable = isClosable && isDismissableProp;
    const isOpen = !isExiting && !isExited;

    const { overlayProps } = useOverlay(
        {
            isDismissable,
            onClose: trigger.close,
            isOpen,
            shouldCloseOnBlur: false,
            shouldCloseOnInteractOutside: (element) =>
                isClosable && shouldCloseOnInteractOutsideOverlay(element),
            isKeyboardDismissDisabled: !isClosable,
        },
        ref
    );

    useContextMenuOutside({
        ref,
        onInteractOutside: () => {
            trigger.close();
        },
        isDisabled: !isOpen || !isDismissable,
    });

    // Get the props to be applied for the trigger to work properly.
    const triggerProps = getOverlayProps(trigger, domProps);

    const content = (
        <div
            ref={ref}
            data-spine-overlay="true"
            tabIndex={-1}
            {...overlayProps}
            {...domProps}
            {...triggerProps}
        />
    );

    // For hover, there is no focus scope.
    if (trigger.action === 'hover') {
        return content;
    }

    return (
        <FocusScope contain={isFocusContained} restoreFocus={restoreFocus} autoFocus={autoFocus}>
            <div
                ref={ref}
                data-spine-overlay="true"
                tabIndex={-1}
                {...overlayProps}
                {...domProps}
                {...triggerProps}
            />
        </FocusScope>
    );
});

/**
 * Check if the overlay should close when an external element is interacted.
 */
export function shouldCloseOnInteractOutsideOverlay(element: Element) {
    return !checkIsElementContainsInOverlay(element);
}

/**
 * Check if the user has interacted outside the element provided as ref.
 */
function useHasInteractedOutside(ref: React.RefObject<HTMLElement>) {
    const [pointed, setPointed] = React.useState(false);
    const documentRef = React.useRef(document);
    useEvent(
        documentRef,
        'pointerdown',
        (event) => {
            const { target } = event;
            const refElement = ref.current;
            setPointed(
                Boolean(
                    target instanceof Element &&
                        refElement &&
                        !refElement.contains(target) &&
                        checkIsElementContainsInOverlay(target)
                )
            );
        },
        { capture: true }
    );
    return pointed;
}
